<template>
  <div class="detail-wrapper">
    <div class="affiche-detail-wrapper" :class="{ dark: night }">
      <van-loading v-if="loading" size="20px">加载中...</van-loading>
      <div v-else>
        <h2 class="title">{{ article.title }}</h2>
        <div class="meta">
          <span class="meta-source">{{ article.source }}</span>
          <span class="meta-time">{{ formatDate(article.time) }}</span>
        </div>
        <DetailPdfEntry
          :file-url="pdfUrl"
          :title="article.title"
          label="点击查看公告pdf原文"
          :night="night"
        ></DetailPdfEntry>
        <div v-if="article.content" ref="content" class="detail-content" v-dompurify-html="article.content"></div>
        <Empty v-else></Empty>
        <div class="disclaimer">{{ disclaimer }}</div>
      </div>
    </div>

    <button
      v-if="showCatalogButton"
      type="button"
      class="catalog-btn"
      :class="{ active: !catalogButtonLocked, 'catalog-btn-dark': night }"
      @click="openCatalog"
    >
      <span class="catalog-btn-inner">
        <img :src="catalogIcon" alt="" />
        <span v-if="catalogButtonLocked" class="catalog-btn-text">目录</span>
      </span>
      <span class="catalog-btn-mask"></span>
    </button>
    <van-popup v-model="showCatalog" position="right" :style="{ width: '80%', height: '100%' }">
      <div class="catalog-drawer" :class="{ dark: night }">
        <div
          v-for="item in catalogs"
          :key="item.id"
          class="catalog-item"
          :class="[`catalog-item-${item.viewLevel}`, { active: item.id === activeCatalogId }]"
          @click="jumpToCatalog(item)"
        >
          <div class="catalog-item-child">
            <div class="catalog-item-text">{{ item.text }}</div>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { materialDetailPage } from '@/api/b2b-news';
import { disclaimer } from '@/views/detailPage/utils/article';
import { getQsid } from '@/views/detailPage/utils/qsid';
import { renderPdfAna } from './utils/pdfAna';
import DetailPdfEntry from '@/views/detailPage/components/DetailPdfEntry/DetailPdfEntry.vue';
import Empty from '@/views/detailPage/components/Empty';
import catalogBtn from '@/assets/images/catalogBtn.png';
import catalogBtnDark from '@/assets/images/catalogBtnDark.png';
import dayjs from 'dayjs';
import { Loading, Popup } from 'vant';

const TIMESTAMP_SECOND_LENGTH = 10;
const MIN_CATALOG_COUNT = 2;
const MAX_CATALOG_LEVEL = 2;
const DEFAULT_ACTIVE_CATALOG_INDEX = 0;
const HEADER_ID_PREFIX = 'hx_';

export default {
  name: 'DetailPageAffiche',
  components: {
    [DetailPdfEntry.name]: DetailPdfEntry,
    [Empty.name]: Empty,
    [Loading.name]: Loading,
    [Popup.name]: Popup,
  },
  data() {
    return {
      loading: false,
      lang: '',
      article: {
        title: '',
        content: '',
      },
      catalogs: [],
      showCatalog: false,
      catalogButtonLocked: true,
      activeCatalogId: '',
      disclaimer,
    };
  },
  computed: {
    night() {
      return window.navigator.userAgent.indexOf('hxtheme/1') > -1 || this.$route.query?.theme === 'black';
    },
    pdfUrl() {
      if (this.article.pdfUrl) {
        return this.article.pdfUrl;
      }
      return Array.isArray(this.article.sourceUrl) ? this.article.sourceUrl[0] || '' : '';
    },
    showCatalogButton() {
      return this.catalogs.length >= MIN_CATALOG_COUNT;
    },
    catalogIcon() {
      return this.night ? catalogBtnDark : catalogBtn;
    },
  },
  mounted() {
    this.initLang();
    this.getDetail();
  },
  methods: {
    initLang() {
      const lang = this.$route.query.lang?.toLowerCase();
      this.lang = lang === 'tw' ? 't' : lang || '';
    },
    getDetail() {
      const { id } = this.$route.params;
      const { accessKey } = this.$route.query;
      const usid = getQsid();
      const params = {
        contentId: id,
        pdfAna: true,
        ...this.lang ? { lang: this.lang } : {},
        ...usid ? { u: usid } : {},
        ...accessKey ? { accessKey } : {},
      };

      this.loading = true;
      materialDetailPage(params).then(res => {
        const { code, data, disclaimer: qsDisclaimer } = res;
        if (code === 0 && data) {
          this.article = {
            ...data,
            content: this.getArticleContent(data),
          };
          this.catalogs = this.getCatalogs(data.pdfAna);
          this.disclaimer = qsDisclaimer || disclaimer;
          this.changeTitle(data.title);
        } else {
          this.resetArticle();
        }
      }).catch(() => {
        this.resetArticle();
      }).finally(() => {
        this.loading = false;
      });
    },
    getArticleContent(data) {
      const pdfAnaContent = renderPdfAna(data.pdfAna);
      return pdfAnaContent || data.content || '';
    },
    getCatalogs(pdfAna = {}) {
      return Array.isArray(pdfAna.catalogs)
        ? pdfAna.catalogs
          .filter(item => item.id && item.text)
          .map(item => ({
            ...item,
            viewLevel: Math.min(Number(item.level) || DEFAULT_ACTIVE_CATALOG_INDEX, MAX_CATALOG_LEVEL),
          }))
        : [];
    },
    resetArticle() {
      this.article = {
        title: '',
        content: '',
      };
      this.catalogs = [];
      this.changeTitle();
    },
    changeTitle(title) {
      window.document.title = title || '公告';
    },
    formatDate(value) {
      if (!value) {
        return '';
      }
      const val = Number(value);
      const time = value.toString().length === TIMESTAMP_SECOND_LENGTH ? dayjs.unix(val) : dayjs(val);
      return time.isValid() ? time.format('YYYY-MM-DD') : '';
    },
    openCatalog() {
      this.catalogButtonLocked = false;
      this.activeCatalogId = this.getActiveCatalogId();
      this.showCatalog = true;
    },
    getActiveCatalogId() {
      const heads = this.$refs.content ? Array.from(this.$refs.content.querySelectorAll('.hx_pdf_header')) : [];
      const activeHead = heads.find(head => {
        const pos = head.getBoundingClientRect();
        return pos.bottom > DEFAULT_ACTIVE_CATALOG_INDEX && pos.bottom < window.innerHeight;
      }) || heads[DEFAULT_ACTIVE_CATALOG_INDEX];
      return activeHead ? Number(activeHead.id.replace(HEADER_ID_PREFIX, '')) : '';
    },
    jumpToCatalog(item) {
      this.showCatalog = false;
      this.$nextTick(() => {
        const target = document.getElementById(`${HEADER_ID_PREFIX}${item.id}`);
        if (target) {
          target.scrollIntoView();
        }
      });
    },
  },
};
</script>

<style lang="less" scoped>
.detail-wrapper {
  min-height: 100vh;
}

.affiche-detail-wrapper {
  min-height: calc(100% - 30px);
  padding: 15px;
  background-color: #ffffff;
  color: #333333;
  font-size: 16px;
  line-height: 24px;

  .title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 30px;
    word-break: break-all;
  }

  .meta {
    display: flex;
    margin-top: 15px;
    color: #a3a3a3;
    font-size: 14px;

    &-source {
      margin-right: 10px;
    }
  }

  .detail-content {
    margin-top: 15px;
    word-break: break-all;
  }

  .disclaimer {
    margin-top: 30px;
    line-height: 22px;
    font-size: 12px;
    color: #a3a3a3;
  }
}

.van-loading {
  height: 100vh;
  line-height: 100px;
  text-align: center;
}

.catalog-btn {
  position: fixed;
  right: 0;
  bottom: 0.675rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0.9rem;
  height: 0.44rem;
  padding: 0;
  border: 0;
  border-radius: 0.5rem 0 0 0.5rem;
  background: #ffffff;
  box-shadow: 0 0 0.08rem 0 rgba(0, 0, 0, 0.2);
  transition-duration: 0.2s;
  transition-property: width, border-radius;
  user-select: none !important;
  z-index: 999;

  img {
    width: 0.22rem;
    height: 0.22rem;
  }

  &.active {
    width: 0.52rem;
  }
}

.catalog-btn-dark {
  background: #191919;
}

.catalog-btn-inner {
  display: flex;
  align-items: center;
}

.catalog-btn-text {
  height: 0.23rem;
  padding-left: 0.11rem;
  color: #e93030;
  font-size: 0.16rem;
  line-height: 0.225rem;
  white-space: nowrap;
}

.catalog-btn-mask {
  position: absolute;
  inset: 0;
}

.catalog-drawer {
  min-height: 100%;
  padding: 20px;
  background: #ffffff;
  box-sizing: border-box;
  text-align: justify;
}

.catalog-item {
  font-size: 0.16rem;

  &:not(:first-of-type) {
    margin-top: 0.305rem;
  }
}

.catalog-item-child {
  position: relative;

  &::after {
    position: absolute;
    right: 0;
    bottom: -0.15rem;
    left: 0;
    height: 0.01rem;
    background-color: #eeeeee;
    content: '';
  }
}

.catalog-item-text {
  display: -webkit-box;
  overflow: hidden;
  color: #323232;
  line-height: 0.225rem;
  text-align: left;
  word-break: break-all;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.catalog-item.active .catalog-item-text {
  color: #e93030;
}

.catalog-item-0 {
  font-size: 0.18rem;

  .catalog-item-text {
    line-height: 0.25rem;
  }

  .catalog-item-child::after {
    left: 0.24rem;
  }
}

.catalog-item-1 {
  padding-left: 0.24rem;
  font-size: 0.18rem;

  .catalog-item-text {
    line-height: 0.25rem;
  }
}

.catalog-item-2 {
  font-size: 0.16rem;

  .catalog-item-text {
    max-height: 0.42rem;
    line-height: 0.225rem;
  }
}

.catalog-item-2 .catalog-item-child {
  padding-left: 0.48rem;
}

.dark {
  background-color: #121212;
  color: #d2d2d2;

  .title {
    color: #d2d2d2;
  }

  .meta {
    color: #a9a9a9;
  }

  .disclaimer {
    color: #8e8e8e;
  }

  .van-loading {
    background-color: #121212;
  }
}

.catalog-drawer.dark {
  background: #121212;

  .catalog-item {
    .catalog-item-child::after {
      background-color: #343434;
    }
  }

  .catalog-item-text {
    color: #d2d2d3;
  }

  .catalog-item.active .catalog-item-text {
    color: #fd4332;
  }
}
</style>
<style lang="less">
.affiche-detail-wrapper {
  .detail-content {
    p {
      margin-top: 10px;
      word-break: break-word;
    }

    img {
      width: 100% !important;
    }

    a {
      display: none;
    }

    .brokers_hidden {
      display: none;
    }

    .textright {
      text-align: right;
    }

    .textcenter {
      text-align: left;
    }

    .hx_pdf_p,
    .wrapper_table,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 0.8em 0;
    }

    h1.hx_pdf_header,
    h2.hx_pdf_header {
      font-size: 100%;
      font-weight: 700;
    }

    .hx_pdf_header {
      color: #323232;
      font-size: 100%;
      font-weight: 400;
    }

    .hx_pdf_p {
      color: #4c4c4c;
      font-size: 100%;
      margin-top: 13px;
      margin-bottom: 13px;
      text-indent: 32px !important;
      word-break: break-all;
      word-wrap: break-word;
    }

    .wrapper_img {
      width: 100%;
    }

    .img_div {
      line-height: 0;
    }

    .hx_pdf_img {
      max-width: 100%;
    }

    .wrapper_table {
      width: 100%;
      overflow-x: auto;
    }

    .hx_pdf_tb {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }

    .hx_pdf_tb td {
      min-width: 50px;
      max-width: 200px;
      padding: 5.6px;
      border: 1px solid #dddddd;
      color: #666666;
      word-wrap: break-word;
    }

    .hx_pdf_tb td span {
      font-size: 100%;
    }

    .hx_pdf_tb tr:first-child td {
      background-color: #f5f5f5;
      color: #999999;
    }

    .hx_pdf_tb tr:nth-child(2n-1) {
      background-color: #f7f7f7;
    }
  }

  &.dark {
    .detail-content {
      .hx_pdf_header,
      .hx_pdf_p {
        color: #d2d2d3;
      }

      .hx_pdf_tb td {
        border-color: #333333;
        color: #a9a9a9;
      }

      .hx_pdf_tb tr:first-child td {
        background-color: #212123;
        color: #8e8e8e;
      }

      .hx_pdf_tb tr:nth-child(2n-1) {
        background-color: #212123;
      }
    }
  }
}
</style>
